// Public /reports/:token page — shows a creator prospect their
// leak exposure and pitches Creator Protect. No auth.

import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';

const API = 'https://tribaltech-backend.onrender.com';

export default function LeakReport() {
  const { token } = useParams();
  const [report, setReport] = useState(null);
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/api/reports/${token}`)
      .then(r => setReport(r.data))
      .catch(e => {
        if (e.response?.status === 410) setErr('This report has expired.');
        else if (e.response?.status === 404) setErr("This report doesn't exist.");
        else setErr(e.response?.data?.error || e.message);
      })
      .finally(() => setLoading(false));
  }, [token]);

  if (loading) return <Shell><div style={{ color:'#888' }}>Loading…</div></Shell>;
  if (err || !report) return (
    <Shell>
      <h1 style={{ color:'#fff', fontSize: 24 }}>Report unavailable</h1>
      <p style={{ color:'#94a3b8', marginTop: 10 }}>{err || 'Not found.'}</p>
      <Link to="/signup" style={ctaButton}>Start a free scan →</Link>
    </Shell>
  );

  const { target_username, total_hits, platforms, hits_by_platform } = report;
  const exposure = total_hits === 0 ? 'clean' : total_hits < 5 ? 'light' : total_hits < 15 ? 'moderate' : 'heavy';
  const headline = total_hits === 0
    ? `Good news — no leaks surfaced for @${target_username}`
    : `${total_hits} leak${total_hits === 1 ? '' : 's'} of @${target_username}'s content are already online`;

  return (
    <Shell>
      <div style={eyebrow}>Creator Protect · Exposure report</div>
      <h1 style={{ color:'#fff', fontSize: 34, margin:'6px 0 8px', lineHeight: 1.15 }}>{headline}</h1>
      <div style={{ color:'#94a3b8', fontSize: 14, marginBottom: 28 }}>
        Generated {new Date(report.created_at).toLocaleDateString()} ·
        Scanned {platforms.length > 0 ? platforms.length : 'multiple'} platform{platforms.length === 1 ? '' : 's'}
      </div>

      {total_hits > 0 && (
        <div style={{ display:'grid', gridTemplateColumns:'repeat(3, 1fr)', gap: 14, marginBottom: 32 }}>
          <Stat label="Leaked URLs found" value={total_hits} color="#f87171" />
          <Stat label="Platforms hosting them" value={platforms.length} color="#fbbf24" />
          <Stat label="Exposure level" value={exposure.toUpperCase()} color={exposure === 'heavy' ? '#f87171' : exposure === 'moderate' ? '#fbbf24' : '#4ade80'} />
        </div>
      )}

      {total_hits > 0 && Object.entries(hits_by_platform).map(([platform, list]) => (
        <div key={platform} style={card}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'baseline', marginBottom: 10 }}>
            <div style={{ fontSize: 13, color:'#a78bfa', letterSpacing: 1, textTransform:'uppercase' }}>{platform}</div>
            <div style={{ fontSize: 12, color:'#94a3b8' }}>{list.length} hit{list.length === 1 ? '' : 's'}</div>
          </div>
          {list.map((h, i) => (
            <div key={i} style={{ padding:'10px 0', borderTop: i === 0 ? 'none' : '1px solid #1a1a1a' }}>
              <a href={h.url} target="_blank" rel="noopener noreferrer" style={{ color:'#cbd5e1', fontSize: 13, wordBreak:'break-all' }}>
                {h.url}
              </a>
              {h.title && (
                <div style={{ fontSize: 12, color:'#666', marginTop: 4 }}>{h.title.slice(0, 140)}</div>
              )}
            </div>
          ))}
        </div>
      ))}

      <div style={{ ...card, marginTop: 30, borderLeft:'3px solid #7c3aed', background:'linear-gradient(180deg, rgba(124,58,237,0.08), rgba(124,58,237,0.02))' }}>
        <div style={{ fontSize: 11, color:'#a78bfa', letterSpacing: 2, textTransform:'uppercase', marginBottom: 8 }}>
          What Creator Protect does
        </div>
        <h2 style={{ color:'#fff', fontSize: 22, margin:'0 0 14px', lineHeight: 1.25 }}>
          {total_hits > 0
            ? 'We file DMCA takedowns on every one of these, automatically.'
            : 'Stay ahead — we scan 30+ piracy platforms every night and file takedowns before leaks spread.'}
        </h2>
        <ul style={{ color:'#cbd5e1', lineHeight: 1.9, fontSize: 14, paddingLeft: 20, marginBottom: 22 }}>
          <li>Automated DMCA filing across 30+ piracy platforms</li>
          <li>Forensic watermarking (coming soon) — every video traceable to the buyer whose copy leaked</li>
          <li>Face Protect (coming soon) — detect impersonators and deepfakes</li>
          <li>VaultDirect — sell directly to fans with built-in protection</li>
          <li>Real-time threat alerts: dox / paste / dark web (coming soon)</li>
        </ul>
        <a href="/signup" style={ctaButton}>
          {total_hits > 0 ? `Start removing these leaks →` : `Start your free trial →`}
        </a>
        <div style={{ fontSize: 12, color:'#666', marginTop: 12 }}>
          3-day free trial · no card required · cancel anytime
        </div>
      </div>

      <div style={{ marginTop: 40, padding: 20, borderTop: '1px solid #1a1a1a', fontSize: 12, color:'#555', textAlign:'center' }}>
        This report was generated from a public-web scan of @{target_username}. Every URL above was found in plain view —
        Creator Protect didn't collect it from anywhere private.
      </div>
    </Shell>
  );
}

function Shell({ children }) {
  return (
    <div style={{ minHeight:'100vh', background:'linear-gradient(180deg, #0f0f1e 0%, #0a0a0f 100%)', color:'#f0ede8', fontFamily: "'DM Sans', -apple-system, sans-serif" }}>
      <div style={{ maxWidth: 820, margin:'0 auto', padding:'48px 20px 60px' }}>
        {children}
      </div>
    </div>
  );
}

function Stat({ label, value, color }) {
  return (
    <div style={{ background:'#111', border:'1px solid #222', borderRadius: 10, padding: 16 }}>
      <div style={{ fontSize: 10, color:'#94a3b8', textTransform:'uppercase', letterSpacing: 1 }}>{label}</div>
      <div style={{ fontSize: 26, fontWeight: 700, color, marginTop: 6 }}>{value}</div>
    </div>
  );
}

const eyebrow = { fontSize: 11, color:'#a78bfa', letterSpacing: 3, textTransform:'uppercase' };
const card = { background:'#111', border:'1px solid #222', borderRadius: 12, padding: 20, marginBottom: 16 };
const ctaButton = {
  display:'inline-block', padding:'14px 28px', background:'#7c3aed', color:'#fff',
  textDecoration:'none', fontWeight:600, fontSize:15, borderRadius:8,
};
